// Implements: F-UIX-023, F-UIX-009, F-UIX-007
/**
 * The scope field. Pure presentation.
 *
 * A native `<select>`: it is keyboard-operable, announced and mobile-friendly
 * for free (F-UIX-007), and a custom listbox would have to earn all of that.
 * The same component serves "change the scope of this mockup" today and
 * "choose the scope of a new one" when a creation form exists.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import type { ScopeOption } from "./scopeOptions";
import styles from "./ScopeSelector.module.css";

export const SCOPE_FIELD_ID = "mockup-scope";

export interface ScopeSelectorProps {
  readonly options: readonly ScopeOption[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean | undefined;
  /** A sentence about the field (why it is disabled, say), announced as its description. */
  readonly hint?: string | undefined;
}

export function ScopeSelector({
  options,
  value,
  onChange,
  disabled = false,
  hint,
}: ScopeSelectorProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Field id={SCOPE_FIELD_ID} label={t("scope.label")} hint={hint}>
      <select
        id={SCOPE_FIELD_ID}
        className={styles.select}
        value={value}
        disabled={disabled}
        aria-describedby={describedBy(SCOPE_FIELD_ID, { hint })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}
